export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
}

export interface ApiResponse<T> {
  data: T
  meta?: {
    page?: number
    pageSize?: number
    total?: number
    totalPages?: number
  }
}

export interface PaginationParams {
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface RequestConfig {
  headers?: Record<string, string>
  params?: Record<string, string | number | boolean | undefined>
  timeout?: number
  signal?: AbortSignal
  /**
   * Request body for verbs that don't take a body argument (e.g. DELETE).
   * Used by `api.delete(url, { data: ... })` to send a JSON payload — required
   * for endpoints like `DELETE /groups/{id}/members` that bulk-delete by id list.
   */
  data?: unknown
  /**
   * Pre-tenant / central anonymous call: suppress Authorization + X-Tenant-ID
   * even if a stale gisify_token / gisify_tenant_id is present in localStorage.
   * Used by the signup funnel (no session, no tenant yet).
   */
  anonymous?: boolean
  /**
   * Send the session's Authorization but NOT X-Tenant-ID, so the API host alone decides the
   * tenant. X-Tenant-ID falls back to the STORED profile's tenant, which is the viewer's tenant,
   * not necessarily the one a shared link points at. Used by the public form fill page.
   */
  omitTenantHeader?: boolean
}

export interface ValidationIssue {
  field: string
  message: string
  code: string
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: Record<string, unknown>,
    public validationErrors?: ValidationIssue[],
    public errorCode?: string,
    /**
     * The whole error body. `constructError` spreads `HttpError.payload` at the
     * TOP level of the response (see `libs/utilities/constructors/lambda.ts`),
     * not under `details`, so structured refusals — the CRS picker's candidate
     * list, for instance — are only reachable from here.
     */
    public body?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}
